import { FormEvent, PropsWithChildren, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { IconButton, Stack, TextField, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Modal } from "components/common/ui/Modal/Modal";
import { useAiChat } from "./useAiChat";

const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 360px;
  overflow-y: auto;
  padding: 4px;
`;

const Bubble = styled.div<{ role: "user" | "assistant" }>`
  align-self: ${(props) => (props.role === "user" ? "flex-end" : "flex-start")};
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px;
  /* User text is plain — preserve their line breaks. Assistant text is
     markdown, which controls its own block layout; pre-wrap there would
     render the source's blank lines as stray vertical gaps. */
  white-space: ${(props) => (props.role === "user" ? "pre-wrap" : "normal")};
  word-break: break-word;
  background: ${(props) => (props.role === "user" ? "#0069a0" : "#f0f2f5")};
  color: ${(props) => (props.role === "user" ? "#ffffff" : "#01314a")};
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

const Dot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 4px;
  border-radius: 50%;
  background: currentColor;
  animation: ${blink} 1.4s infinite both;
  &:nth-of-type(2) {
    animation-delay: 0.2s;
  }
  &:nth-of-type(3) {
    animation-delay: 0.4s;
  }
`;

/** Animated "more to come" indicator shown on the streaming assistant bubble. */
function TypingDots() {
  return (
    <span
      role="status"
      aria-label="Assistant is typing"
      data-testid="ai-chat-typing"
    >
      <Dot />
      <Dot />
      <Dot />
    </span>
  );
}

/**
 * Element renderers for assistant markdown. Kept minimal: tighten paragraph
 * spacing so multi-paragraph answers don't balloon the bubble, and open links
 * in a new tab with a safe rel. Bold/italic/lists fall through to defaults.
 */
const markdownComponents = {
  p: ({ children }: PropsWithChildren) => (
    <Typography component="p" sx={{ m: 0, "& + &": { mt: 0.75 } }}>
      {children}
    </Typography>
  ),
  ul: ({ children }: PropsWithChildren) => (
    <Typography component="ul" sx={{ m: 0, pl: 2.5 }}>
      {children}
    </Typography>
  ),
  ol: ({ children }: PropsWithChildren) => (
    <Typography component="ol" sx={{ m: 0, pl: 2.5 }}>
      {children}
    </Typography>
  ),
  // `& > p` flattens the paragraph CommonMark wraps each item in for a
  // "loose" list, so items stay single-spaced instead of doubly padded.
  li: ({ children }: PropsWithChildren) => (
    <Typography component="li" sx={{ m: 0, "& > p": { m: 0 } }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }: PropsWithChildren<{ href?: string }>) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

/**
 * Assistant text arrives as markdown from pcs-ai-agents, so render it instead of
 * showing literal syntax like `**date**`. We deliberately do NOT enable
 * rehype-raw: this is model-generated output, and react-markdown escapes any
 * embedded HTML by default, which keeps the XSS surface closed.
 */
function AssistantMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
      {content}
    </ReactMarkdown>
  );
}

export interface AiChatModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Customer-facing AI chat. Streams answers from pcs-ai-agents over SSE and
 * renders them incrementally with a typing indicator until the turn finishes.
 * Styling is intentionally light — it leans on the shared Modal and MUI theme.
 */
export function AiChatModal({ open, onClose }: AiChatModalProps) {
  const { messages, input, setInput, isStreaming, error, send, cancel } =
    useAiChat();
  const listEndRef = useRef<HTMLDivElement>(null);

  // Keep the latest content in view as deltas stream in.
  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages]);

  function handleClose() {
    cancel();
    onClose();
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void send();
  }

  const lastIndex = messages.length - 1;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="PCS Move Assistant"
      titleIcon={<SmartToyOutlinedIcon color="primary" />}
      width={480}
    >
      <MessageList data-testid="ai-chat-messages">
        {messages.map((message, index) => {
          const streamingThis =
            isStreaming && index === lastIndex && message.role === "assistant";
          return (
            <Bubble key={message.id} role={message.role}>
              {message.role === "assistant" ? (
                <AssistantMarkdown content={message.content} />
              ) : (
                message.content
              )}
              {streamingThis ? <TypingDots /> : null}
            </Bubble>
          );
        })}
        <div ref={listEndRef} />
      </MessageList>

      {error ? (
        <Typography role="alert" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      ) : null}

      <form onSubmit={handleSubmit}>
        <Stack direction="row" gap={1} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask a question about your move…"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            disabled={isStreaming}
            inputProps={{ "aria-label": "Message" }}
          />
          <IconButton
            type="submit"
            color="primary"
            disabled={isStreaming || input.trim() === ""}
            aria-label="Send"
          >
            <SendIcon />
          </IconButton>
        </Stack>
      </form>
    </Modal>
  );
}
